import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  Check,
  ArrowLeft,
  ArrowRight,
  Calendar as CalendarIcon,
  Users,
  Clock,
  MapPin,
  Leaf,
  Mountain,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TreePine,
  Sun,
  Coffee,
  Footprints,
} from "lucide-react";

// Note: hike_experience.png doesn't exist, using crafting process image as fallback
import workshopImage from "@assets/generated_images/terrarium_crafting_process.png";
import ryanArtisan from "@assets/generated_images/ryan_the_artisan_maker.png";
// Use workshop image as fallback for hike image
const hikeImage = workshopImage;

interface WorkshopAvailability {
  date: string;
  isAvailable: boolean;
  isWeekend: boolean;
  reason?: string;
}

interface WorkshopPricing {
  packages: {
    id: string;
    name: string;
    price: number;
    description: string;
    includes: string[];
  }[];
  workshopDetails: {
    duration: string;
    location: string;
    whatToBring: string[];
    included: string[];
  };
}

const STEPS = [
  { id: 1, name: "Package" },
  { id: 2, name: "Date" },
  { id: 3, name: "Details" },
  { id: 4, name: "Review" },
];

interface BookingState {
  step: number;
  packageType: "2-person" | "4-person" | null;
  selectedDate: Date | null;
  name: string;
  email: string;
  phone: string;
  specialRequests: string;
}

function ProgressIndicator({ currentStep }: { currentStep: number }) {
  return (
    <nav className="w-full max-w-2xl mx-auto px-4 py-4" aria-label="Booking progress">
      <ol className="flex items-center justify-between" role="list">
        {STEPS.map((step, index) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;

          return (
            <li
              key={step.id}
              className="flex items-center flex-1"
              aria-current={isCurrent ? "step" : undefined}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                    isCompleted
                      ? "bg-primary text-primary-foreground"
                      : isCurrent
                      ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2"
                      : "bg-muted text-muted-foreground"
                  }`}
                  aria-hidden="true"
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : step.id}
                </div>
                <span
                  className={`text-xs mt-1.5 font-medium ${
                    currentStep >= step.id ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  <span className="sr-only">
                    {isCompleted ? "Completed: " : isCurrent ? "Current step: " : "Upcoming: "}
                  </span>
                  {step.name}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 transition-colors ${
                    currentStep > step.id ? "bg-primary" : "bg-muted"
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function Workshop() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [bookingState, setBookingState] = useState<BookingState>({
    step: 1,
    packageType: null,
    selectedDate: null,
    name: "",
    email: "",
    phone: "",
    specialRequests: "",
  });

  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Check for cancelled booking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled") === "true") {
      toast({
        title: "Booking cancelled",
        description: "Your workshop booking was cancelled. You can try again anytime.",
      });
    }
  }, [toast]);

  // Fetch pricing
  const { data: pricing } = useQuery<WorkshopPricing>({
    queryKey: ["/api/workshop/pricing"],
  });

  // Fetch availability for current month
  const { data: availability, isLoading: loadingAvailability } = useQuery<WorkshopAvailability[]>({
    queryKey: ["/api/workshop/availability", currentMonth.getMonth(), currentMonth.getFullYear()],
    queryFn: async () => {
      const response = await fetch(
        `/api/workshop/availability?month=${currentMonth.getMonth()}&year=${currentMonth.getFullYear()}`
      );
      if (!response.ok) throw new Error("Failed to fetch availability");
      return response.json();
    },
  });

  // Create booking mutation
  const bookingMutation = useMutation({
    mutationFn: async (data: {
      customerName: string;
      customerEmail: string;
      customerPhone?: string;
      workshopDate: string;
      packageType: string;
      specialRequests?: string;
    }) => {
      const response = await apiRequest("POST", "/api/workshop/book", data);
      const result = await response.json() as { checkoutUrl: string; bookingId: string; sessionId: string };
      return result;
    },
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Booking failed",
        description: error.message || "Failed to create booking. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateState = (updates: Partial<BookingState>) => {
    setBookingState((prev) => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    updateState({ step: bookingState.step + 1 });
  };

  const handleBack = () => {
    updateState({ step: bookingState.step - 1 });
  };

  // Create set of available dates for calendar
  const availableDates = useMemo(() => {
    if (!availability) return new Set<string>();
    return new Set(
      availability.filter((a) => a.isAvailable).map((a) => a.date)
    );
  }, [availability]);

  const handleSubmit = () => {
    if (!bookingState.selectedDate || !bookingState.packageType) return;

    bookingMutation.mutate({
      customerName: bookingState.name,
      customerEmail: bookingState.email,
      customerPhone: bookingState.phone || undefined,
      workshopDate: bookingState.selectedDate.toISOString().split("T")[0],
      packageType: bookingState.packageType,
      specialRequests: bookingState.specialRequests || undefined,
    });
  };

  const getPrice = () => {
    if (!bookingState.packageType || !pricing) return 0;
    const pkg = pricing.packages.find((p) => p.id === bookingState.packageType);
    return pkg?.price || 0;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderStep = () => {
    switch (bookingState.step) {
      case 1:
        return (
          <Step1Package
            bookingState={bookingState}
            updateState={updateState}
            pricing={pricing}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <Step2Date
            bookingState={bookingState}
            updateState={updateState}
            availability={availability}
            availableDates={availableDates}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            loadingAvailability={loadingAvailability}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <Step3Details
            bookingState={bookingState}
            updateState={updateState}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <Step4Review
            bookingState={bookingState}
            pricing={pricing}
            getPrice={getPrice}
            formatDate={formatDate}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={bookingMutation.isPending}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Hero Section */}
      <header role="banner" className="relative h-64 md:h-80 overflow-hidden">
        <OptimizedImage
          src={hikeImage}
          alt="Terrarium Workshop Experience"
          fill
          objectFit="cover"
          priority
          className="absolute inset-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background z-10" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 z-20">
          <h1 className="font-serif text-3xl md:text-5xl font-bold text-center mb-3">
            Workshop Experience
          </h1>
          <p className="text-lg md:text-xl text-white/90 text-center max-w-2xl">
            A full-day terrarium building adventure with Ryan
          </p>
        </div>
      </header>

      {/* Progress */}
      <ProgressIndicator currentStep={bookingState.step} />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 pb-16" role="main" aria-label="Workshop booking form">
        <AnimatePresence mode="wait">
          <motion.div
            key={bookingState.step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// Step 1: Package Selection
function Step1Package({
  bookingState,
  updateState,
  pricing,
  onNext,
}: {
  bookingState: BookingState;
  updateState: (updates: Partial<BookingState>) => void;
  pricing?: WorkshopPricing;
  onNext: () => void;
}) {
  const packages = pricing?.packages || [
    {
      id: "2-person",
      name: "Duo Workshop Experience",
      price: 2000,
      description: "Full-day workshop for 2 people",
      includes: ["Guided nature hike", "All materials", "Lunch provided", "Take home terrariums"],
    },
    {
      id: "4-person",
      name: "Group Workshop Experience",
      price: 4000,
      description: "Full-day workshop for 4 people",
      includes: ["Guided nature hike", "All materials", "Lunch provided", "Take home terrariums", "Perfect for groups"],
    },
  ];

  return (
    <div className="space-y-8">
      {/* What's Included */}
      <Card className="p-6 bg-primary/5 border-primary/20">
        <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          What's Included
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Footprints className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Guided Nature Hike</p>
              <p className="text-sm text-muted-foreground">Collect materials from Colorado's beautiful landscapes</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Full Day Experience</p>
              <p className="text-sm text-muted-foreground">9am - 5pm workshop with Ryan</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Coffee className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Lunch Provided</p>
              <p className="text-sm text-muted-foreground">Enjoy a delicious meal during your creative day</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <TreePine className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Take Home Terrariums</p>
              <p className="text-sm text-muted-foreground">Create and keep your custom terrarium</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Package Selection */}
      <div>
        <h2 className="font-serif text-2xl font-bold mb-6 text-center">
          Choose Your Workshop Package
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`p-6 cursor-pointer transition-all hover:shadow-lg ${
                bookingState.packageType === pkg.id
                  ? "ring-2 ring-primary border-primary"
                  : "hover:border-primary/50"
              }`}
              onClick={() => updateState({ packageType: pkg.id as "2-person" | "4-person" })}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-lg">{pkg.name}</h3>
                    <p className="text-sm text-muted-foreground">{pkg.description}</p>
                  </div>
                </div>
                {bookingState.packageType === pkg.id && (
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>

              <Separator className="my-4" />

              <ul className="space-y-2 mb-4">
                {pkg.includes.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="text-right">
                <span className="font-serif text-2xl font-bold text-primary">
                  ${pkg.price.toLocaleString()}
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Continue Button */}
      <div className="flex justify-end">
        <Button
          size="lg"
          onClick={onNext}
          disabled={!bookingState.packageType}
          className="gap-2"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Step 2: Date Selection
function Step2Date({
  bookingState,
  updateState,
  availability,
  availableDates,
  currentMonth,
  setCurrentMonth,
  loadingAvailability,
  onNext,
  onBack,
}: {
  bookingState: BookingState;
  updateState: (updates: Partial<BookingState>) => void;
  availability?: WorkshopAvailability[];
  availableDates: Set<string>;
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
  loadingAvailability: boolean;
  onNext: () => void;
  onBack: () => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const dateString = date.toISOString().split("T")[0];
      if (availableDates.has(dateString)) {
        updateState({ selectedDate: date });
      }
    }
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    const newMonth = new Date(currentMonth);
    if (direction === "prev") {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  // Disable non-weekend and unavailable dates
  const isDateDisabled = (date: Date) => {
    const dateString = date.toISOString().split("T")[0];
    return !availableDates.has(dateString);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="font-serif text-2xl font-bold mb-2">
          Select Your Workshop Date
        </h2>
        <p className="text-muted-foreground">
          Workshops are available on weekends. Green dates are available.
        </p>
      </div>

      <Card className="p-6 max-w-md mx-auto">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleMonthChange("prev")}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h3 className="font-serif font-semibold text-lg">
            {currentMonth.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </h3>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleMonthChange("next")}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {loadingAvailability ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <Calendar
            mode="single"
            selected={bookingState.selectedDate || undefined}
            onSelect={handleDateSelect}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            disabled={isDateDisabled}
            modifiers={{
              available: (date) => {
                const dateString = date.toISOString().split("T")[0];
                return availableDates.has(dateString);
              },
            }}
            modifiersClassNames={{
              available: "bg-green-100 text-green-800 hover:bg-green-200 font-semibold",
            }}
            className="rounded-md"
          />
        )}

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
            <span className="text-sm text-muted-foreground">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-muted border" />
            <span className="text-sm text-muted-foreground">Unavailable</span>
          </div>
        </div>
      </Card>

      {/* Selected Date Display */}
      {bookingState.selectedDate && (
        <Card className="p-4 bg-primary/5 border-primary/20 max-w-md mx-auto">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium">Selected Date</p>
              <p className="text-primary font-semibold">
                {bookingState.selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          size="lg"
          onClick={onNext}
          disabled={!bookingState.selectedDate}
          className="gap-2"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Step 3: Contact Details
function Step3Details({
  bookingState,
  updateState,
  onNext,
  onBack,
}: {
  bookingState: BookingState;
  updateState: (updates: Partial<BookingState>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const isValid =
    bookingState.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingState.email);

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="font-serif text-2xl font-bold mb-2">Your Details</h2>
        <p className="text-muted-foreground">
          Tell us a bit about yourself so we can prepare for your visit
        </p>
      </div>

      <Card className="p-6 max-w-md mx-auto">
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              placeholder="Your name"
              value={bookingState.name}
              onChange={(e) => updateState({ name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={bookingState.email}
              onChange={(e) => updateState({ email: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number (optional)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 555-5555"
              value={bookingState.phone}
              onChange={(e) => updateState({ phone: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="requests">Special Requests (optional)</Label>
            <Textarea
              id="requests"
              placeholder="Any dietary restrictions, accessibility needs, or special requests..."
              value={bookingState.specialRequests}
              onChange={(e) => updateState({ specialRequests: e.target.value })}
              rows={4}
            />
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          size="lg"
          onClick={onNext}
          disabled={!isValid}
          className="gap-2"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Step 4: Review and Pay
function Step4Review({
  bookingState,
  pricing,
  getPrice,
  formatDate,
  onSubmit,
  onBack,
  isSubmitting,
}: {
  bookingState: BookingState;
  pricing?: WorkshopPricing;
  getPrice: () => number;
  formatDate: (date: Date) => string;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const pkg = pricing?.packages.find((p) => p.id === bookingState.packageType);
  const price = getPrice();

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="font-serif text-2xl font-bold mb-2">Review Your Booking</h2>
        <p className="text-muted-foreground">
          Please confirm your details before proceeding to payment
        </p>
      </div>

      <Card className="p-6 max-w-lg mx-auto">
        <div className="space-y-6">
          {/* Package */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Workshop Package
            </h3>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary" />
              <span className="font-medium">{pkg?.name || bookingState.packageType}</span>
            </div>
          </div>

          <Separator />

          {/* Date */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Workshop Date
            </h3>
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-primary" />
              <span className="font-medium">
                {bookingState.selectedDate ? formatDate(bookingState.selectedDate) : "Not selected"}
              </span>
            </div>
          </div>

          <Separator />

          {/* Time */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Workshop Time
            </h3>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <span className="font-medium">9:00 AM - 5:00 PM (approximately)</span>
            </div>
          </div>

          <Separator />

          {/* Contact */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Contact Details
            </h3>
            <div className="space-y-1">
              <p className="font-medium">{bookingState.name}</p>
              <p className="text-muted-foreground">{bookingState.email}</p>
              {bookingState.phone && (
                <p className="text-muted-foreground">{bookingState.phone}</p>
              )}
            </div>
          </div>

          {bookingState.specialRequests && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Special Requests
                </h3>
                <p className="text-sm">{bookingState.specialRequests}</p>
              </div>
            </>
          )}

          <Separator />

          {/* Total */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-lg font-medium">Total</span>
            <span className="font-serif text-3xl font-bold text-primary">
              ${price.toLocaleString()}
            </span>
          </div>
        </div>
      </Card>

      {/* What to Expect */}
      <Card className="p-6 max-w-lg mx-auto bg-muted/50">
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />
          What to Expect
        </h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>Meeting location details will be sent via email after booking</li>
          <li>Wear comfortable hiking shoes and weather-appropriate clothing</li>
          <li>Bring a water bottle (lunch and snacks are provided)</li>
          <li>You'll take home your completed terrarium(s)</li>
        </ul>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          size="lg"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Processing...
            </>
          ) : (
            <>
              Proceed to Payment
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
